import type { LucideIcon } from "lucide-react";
import {
  CalendarIcon,
  ClipboardCheckIcon,
  ClockIcon,
  ListTodoIcon,
  UsersIcon,
} from "lucide-react";

import { DashboardTaskAddButton } from "./DashboardTaskAddButton";

import { Row } from "@/components/Row";
import { Text } from "@/components/Text";

import { cn } from "@/lib/utils";
import { DashboardScheduleTaskCategory } from "@/types";

const categoryChipConfig: Record<
  DashboardScheduleTaskCategory,
  {
    Icon: LucideIcon;
    label: string;
    chipClassName: string;
    iconClassName: string;
  }
> = {
  // Untyped user tasks; not surfaced in the counts band, kept for exhaustiveness.
  [DashboardScheduleTaskCategory.GENERAL]: {
    Icon: ListTodoIcon,
    label: "General",
    chipClassName: "border-slate-200 bg-slate-50 text-slate-700",
    iconClassName: "text-slate-600",
  },
  [DashboardScheduleTaskCategory.CHECK_INS]: {
    Icon: ClipboardCheckIcon,
    label: "Check-ins",
    chipClassName: "border-amber-200 bg-amber-50 text-amber-800",
    iconClassName: "text-amber-700",
  },
  [DashboardScheduleTaskCategory.STARTS]: {
    Icon: UsersIcon,
    label: "Starts",
    chipClassName: "border-green-200 bg-green-50 text-green-800",
    iconClassName: "text-green-700",
  },
  [DashboardScheduleTaskCategory.INTERVIEWS]: {
    Icon: CalendarIcon,
    label: "Interviews",
    chipClassName: "border-blue-200 bg-blue-50 text-blue-800",
    iconClassName: "text-blue-700",
  },
  [DashboardScheduleTaskCategory.MISSING_START]: {
    Icon: ClockIcon,
    label: "Missing start",
    chipClassName: "border-red-200 bg-red-50 text-red-700",
    iconClassName: "text-red-600",
  },
};

// Definitive section header for the expanded task list — icon + label on a
// slightly raised background.
export const DashboardScheduleWeekTaskCategoryHeader = ({
  category,
  showAddButton = false,
  onAddClick,
}: {
  category: DashboardScheduleTaskCategory;
  showAddButton?: boolean;
  onAddClick?: () => void;
}) => {
  const config = categoryChipConfig[category];
  const Icon = config.Icon;

  const render = () => (
    <Row className="items-center justify-between border-b border-slate-100 bg-slate-50/80 px-4 py-2">
      <Row className="items-center gap-1.5">
        <Icon className={cn("h-3.5 w-3.5 shrink-0", config.iconClassName)} />
        <Text className="text-xs font-semibold text-slate-600">
          {config.label}
        </Text>
      </Row>
      {showAddButton && onAddClick ? (
        <DashboardTaskAddButton onClick={onAddClick} />
      ) : null}
    </Row>
  );

  return render();
};

export const DashboardScheduleWeekTaskCategoryChip = ({
  category,
  count,
}: {
  category: DashboardScheduleTaskCategory;
  count: number;
}) => {
  const config = categoryChipConfig[category];
  const Icon = config.Icon;

  const render = () => (
    <Row
      className={cn(
        "inline-flex max-w-full shrink-0 items-center gap-1 rounded-full border px-2 py-0.5",
        config.chipClassName,
      )}
    >
      <Icon className={cn("h-3 w-3 shrink-0", config.iconClassName)} />
      <Text className="truncate text-[11px] font-medium leading-tight">
        {count.toString()} {config.label}
      </Text>
    </Row>
  );

  return render();
};
